import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { LifeBuoy, Send, Plus, Clock, CheckCircle2, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = '/api';

const PRIORITY_META = {
  low: { label: 'Nízká', color: 'bg-slate-100 text-slate-800 border-slate-200' },
  medium: { label: 'Střední', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  high: { label: 'Vysoká', color: 'bg-red-100 text-red-800 border-red-200' },
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}

export default function ITServisPage() {
  const [statuses, setStatuses] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/it/statuses`, { withCredentials: true }),
        axios.get(`${API}/it/requests/my`, { withCredentials: true }),
      ]);
      setStatuses(s.data || []);
      setMyRequests(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      toast.error('Vyplňte předmět i popis');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/it/requests`, { subject, description, priority }, { withCredentials: true });
      toast.success('Požadavek odeslán');
      setSubject(''); setDescription(''); setPriority('medium');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při odesílání');
    } finally {
      setSubmitting(false);
    }
  };

  const statusMap = Object.fromEntries(statuses.map(s => [s.id, s]));

  return (
    <Layout>
      <div className="space-y-6" data-testid="it-servis-page">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope'] flex items-center gap-3">
            <LifeBuoy className="w-8 h-8 text-[#16A34A]" /> IT — Požadavek servis
          </h1>
          <p className="text-[#57534E] mt-1">Zadejte požadavek na IT servis. Vaše dotazy vyřídí IT tým.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Form */}
          <Card className="bg-white border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#16A34A]" /> Nový požadavek
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label className="text-[#57534E] text-xs">Předmět *</Label>
                  <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Krátký nadpis..." data-testid="it-subject" maxLength={140} required className="mt-1" />
                </div>
                <div>
                  <Label className="text-[#57534E] text-xs">Popis problému *</Label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={6}
                    placeholder="Popište co nejpřesněji problém, kdy nastává, jaké kroky jste zkusili..."
                    data-testid="it-description"
                    required
                    className="mt-1 w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#1C1917] focus:border-[#16A34A] focus:outline-none focus:ring-1 focus:ring-[#16A34A]"
                  />
                </div>
                <div>
                  <Label className="text-[#57534E] text-xs">Priorita</Label>
                  <div className="flex gap-2 mt-1">
                    {Object.entries(PRIORITY_META).map(([key, meta]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setPriority(key)}
                        data-testid={`priority-${key}`}
                        className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                          priority === key
                            ? 'bg-[#052E16] text-white border-[#052E16]'
                            : `${meta.color} hover:bg-opacity-70`
                        }`}
                      >
                        {meta.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Button type="submit" disabled={submitting} data-testid="it-submit" className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white">
                  <Send className="w-4 h-4 mr-2" /> {submitting ? 'Odesílám...' : 'Odeslat požadavek'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* My requests */}
          <Card className="bg-white border-[#E2E8F0] shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#16A34A]" /> Moje požadavky
                <span className="text-xs font-medium text-[#94A3B8]">({myRequests.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-[#94A3B8] py-4 text-center">Načítám...</p>
              ) : myRequests.length === 0 ? (
                <p className="text-sm text-[#94A3B8] py-6 text-center">Zatím jste nezadali žádný požadavek</p>
              ) : (
                <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                  {myRequests.map(r => {
                    const status = statusMap[r.status_id];
                    const pr = PRIORITY_META[r.priority] || PRIORITY_META.medium;
                    return (
                      <div key={r.id} className="p-3 rounded-lg border border-[#E2E8F0] bg-white hover:bg-[#F8FAF8]" data-testid={`my-req-${r.id}`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-[#1C1917] truncate">{r.subject}</p>
                            <p className="text-[11px] text-[#94A3B8] mt-0.5">{fmtDateTime(r.created_at)}</p>
                          </div>
                          <div className="flex flex-col gap-1 items-end shrink-0">
                            {status && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border" style={{ backgroundColor: status.color + '20', color: status.color, borderColor: status.color + '55' }}>
                                {status.name}
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold border ${pr.color}`}>
                              {pr.label}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-[#57534E] mt-2 line-clamp-2">{r.description}</p>
                        {r.owner_name && (
                          <p className="text-[10px] text-[#94A3B8] mt-1.5">Řeší: <strong className="text-[#1C1917]">{r.owner_name}</strong></p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
